/* JobView: renders a folded job (lib/job-reducer) as one assistant reply: named status, collapsed
   steps, streaming Markdown with inline citation chips, approvals, artifacts, sources and the model
   attribution footer. Features that stream a job can drop this in instead of wiring each piece. */
import type { ReactNode } from 'react';
import { PanelRightOpen, Sheet as SheetIcon, Sparkles } from 'lucide-react';
import type { ApprovalDecision, Citation } from '../lib/api-types';
import { researchProgressOf, type JobState } from '../lib/job-reducer';
import type { StreamStatus } from '../lib/sse';
import { ApprovalCard } from './ApprovalCard';
import { Banner, Button, Chip } from './basics';
import { SourceStrip } from './Citations';
import { Message, type MessageActions } from './Message';
import { ToolSteps, fmtDuration, type ToolStepSpec } from './ToolStep';
import { ResearchProgress } from './ResearchProgress';

export function jobSteps(s: JobState): ToolStepSpec[] {
  return s.steps.map((st) => {
    const call = st.toolCallIds.length ? s.toolCalls[st.toolCallIds[0]!] : undefined;
    const kind = st.kind === 'tool' && call ? call.name : st.kind;
    let target = st.target;
    if (!target && call?.args && typeof call.args === 'object') {
      const v = Object.values(call.args as Record<string, unknown>)[0];
      if (typeof v === 'string') target = `"${v}"`;
    }
    const detail = call?.result !== undefined
      ? <pre>{typeof call.result === 'string' ? call.result : JSON.stringify(call.result, null, 2)}</pre>
      : undefined;
    return { id: st.id, label: st.label, target, kind, status: st.status, durationMs: st.durationMs, detail };
  });
}

export interface JobViewProps {
  state: JobState;
  connection?: StreamStatus | 'idle';
  onApprove?: (approvalId: string, decision: ApprovalDecision, extra: { allowForTask?: boolean; editedPreview?: string; allowTask?: boolean }) => void;
  onOpenSource?: (c: Citation) => void;
  onOpenSources?: () => void;
  onOpenArtifact?: (id: string) => void;
  onRetry?: () => void;
  onContinue?: () => void;
  actions?: MessageActions;
  latest?: boolean;
  children?: ReactNode;
}

export function JobView({ state: s, connection, onApprove, onOpenSource, onOpenSources, onOpenArtifact, onRetry, onContinue, actions, latest, children }: JobViewProps) {
  const running = s.status === 'running' || s.status === 'waiting' || s.status === 'idle';
  const now = s.steps.find((x) => x.status === 'running');
  const status = connection === 'reconnecting' ? 'Reconnecting…'
    : s.status === 'waiting' ? 'Waiting for your approval'
    : now ? `${now.label}…` : running ? 'Starting…' : undefined;
  const elapsed = s.startedAt && s.finishedAt ? s.finishedAt - s.startedAt : undefined;
  const research = researchProgressOf(s);
  return (
    <Message
      role="assistant"
      text={s.text}
      streaming={running && !!s.text && !s.textDone}
      status={running ? status : undefined}
      latest={latest}
      credits={s.usage.credits}
      stopped={s.status === 'cancelled'}
      onContinue={onContinue}
      actions={{ onCheckSources: onOpenSources, ...actions }}
      steps={<ToolSteps steps={jobSteps(s)} reasoning={s.reasoning || undefined} durationMs={elapsed} running={running} />}
      error={s.status === 'error' && s.error ? (
        <Banner tone="danger" title="This reply stopped early" action={onRetry && s.error.retryable !== false ? <Button size="sm" onClick={onRetry}>Retry</Button> : undefined}>
          {s.error.message} {s.text ? 'The part written so far is kept above.' : ''}
        </Banner>
      ) : undefined}
      extras={
        <>
          {research && running && <div style={{ marginTop: 12 }}><ResearchProgress {...research} /></div>}
          {s.approvals.map((a) => (
            <div key={a.id} style={{ marginTop: 12 }}>
              <ApprovalCard
                approval={a}
                state={a.status}
                onDecision={(d, extra) => onApprove?.(a.id, d, { ...extra, allowTask: a.allowTask })}
                resolvedNote={a.by ? `by ${a.by}` : undefined}
              />
            </div>
          ))}
          {s.artifacts.map((a) => (
            <div key={a.id} className="card" style={{ marginTop: 12, padding: 0, overflow: 'hidden' }} data-cap="CHAT-06">
              <div className="artp-hd">
                <SheetIcon size={16} aria-hidden="true" /><b>{a.title}</b><Chip>{a.kind}</Chip><Chip>v{a.version}</Chip>
                <span className="sp" />
                {onOpenArtifact && <Button size="sm" icon={<PanelRightOpen size={14} aria-hidden="true" />} onClick={() => onOpenArtifact(a.id)}>Open beside chat</Button>}
              </div>
              {a.content && <pre style={{ margin: 0, borderRadius: 0, maxHeight: 160 }}>{a.content}</pre>}
            </div>
          ))}
          {children}
          <SourceStrip citations={s.citations} onOpen={onOpenSource} onOpenAll={onOpenSources} />
        </>
      }
      footer={s.model || elapsed ? (
        <span className="row" style={{ gap: 4 }} data-cap="MODEL-03"><Sparkles size={14} aria-hidden="true" /> {s.model ?? 'Auto'}{elapsed ? ` · ${fmtDuration(elapsed)}` : ''}</span>
      ) : undefined}
      citations={s.citations}
      onCite={onOpenSource}
    />
  );
}
